import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/app/providers';
import type { SupplierVisit, SupplierVisitParams, RegisterVisitDTO, CheckoutVisitDTO, RateVisitDTO } from '@yawi/types';
import type { PaginatedResponse } from '@yawi/domain';

export function useSupplierVisits(supplierId?: string, initialParams?: SupplierVisitParams) {
  const { supplierRepo } = useServices();
  const [data, setData] = useState<PaginatedResponse<SupplierVisit> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<SupplierVisitParams>(initialParams || { page: 1, limit: 20 });

  const fetchVisits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = supplierId
        ? await supplierRepo.getVisits(supplierId, params)
        : await supplierRepo.getAllVisits(params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar visitas');
    } finally {
      setLoading(false);
    }
  }, [supplierRepo, supplierId, params]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  const filterByDateRange = useCallback((startDate: string, endDate: string) => {
    setParams(prev => ({ ...prev, startDate, endDate, page: 1 }));
  }, []);

  /**
   * Búsqueda en el servidor. Filtrar la página en memoria dejaba la página
   * incompleta y el total del `meta` describiendo otra cosa; además hay que
   * volver a la 1 porque el conjunto encogió.
   */
  const searchVisits = useCallback((search: string) => {
    setParams(prev => ({ ...prev, search: search || undefined, page: 1 }));
  }, []);

  const goToPage = useCallback((page: number) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  const registerVisit = useCallback(async (targetSupplierId: string, data: RegisterVisitDTO) => {
    const newVisit = await supplierRepo.registerVisit(targetSupplierId, data);
    fetchVisits();
    return newVisit;
  }, [supplierRepo, fetchVisits]);

  const checkoutVisit = useCallback(async (targetSupplierId: string, visitId: string, data?: CheckoutVisitDTO) => {
    const updated = await supplierRepo.checkoutVisit(targetSupplierId, visitId, data);
    fetchVisits();
    return updated;
  }, [supplierRepo, fetchVisits]);

  const rateVisit = useCallback(async (targetSupplierId: string, visitId: string, data: RateVisitDTO) => {
    const updated = await supplierRepo.rateVisit(targetSupplierId, visitId, data);
    fetchVisits();
    return updated;
  }, [supplierRepo, fetchVisits]);

  return {
    visits: data?.data || [],
    pagination: data?.meta,
    loading,
    error,
    params,
    filterByDateRange,
    searchVisits,
    goToPage,
    registerVisit,
    checkoutVisit,
    rateVisit,
    refresh: fetchVisits,
  };
}
